/**
 * EventRSVPView — Event RSVP confirmation for scanned intent codes
 *
 * Shows event details and an RSVP form (name + email). On success,
 * shows confirmation. Mobile-first — visitors will be on their phones.
 *
 * @story SCRUM-1450 — 0.1.1.3-02: Render Directive Frontend Integration
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

function formatEventDate(startsAt) {
  if (!startsAt) {
    return '';
  }
  const d = new Date(startsAt);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatEventTime(startsAt, endsAt) {
  if (!startsAt) {
    return '';
  }
  const opts = { hour: 'numeric', minute: '2-digit' };
  const start = new Date(startsAt).toLocaleTimeString('en-US', opts);
  if (!endsAt) {
    return start;
  }
  const end = new Date(endsAt).toLocaleTimeString('en-US', opts);
  return `${start} – ${end}`;
}

export default function EventRSVPView({ event, intentCode, marketSlug: _marketSlug }) {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/events/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent_code: intentCode,
          email: email.trim(),
          first_name: firstName.trim(),
          consent_given: consentGiven,
        }),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-emerald-500 text-5xl mb-4" aria-hidden="true">&#10003;</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            You&apos;re in!
          </h1>
          <p className="text-gray-600">
            See you at {event?.name || 'the event'}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-900 via-emerald-800 to-gray-900 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {event?.name || 'Event RSVP'}
        </h1>

        {event?.starts_at && (
          <p className="text-gray-600 text-sm mb-1">
            {formatEventDate(event.starts_at)}
          </p>
        )}
        {event?.starts_at && (
          <p className="text-gray-600 text-sm mb-1">
            {formatEventTime(event.starts_at, event.ends_at)}
          </p>
        )}
        {event?.location_name && (
          <p className="text-gray-600 text-sm mb-4">
            {event.location_name}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 mt-4">
          <input
            type="text"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            aria-label="First name"
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            aria-label="Email"
            required
          />
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              id="consent-checkbox"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              className="mt-0.5 accent-emerald-600"
              data-testid="consent-checkbox"
            />
            <label htmlFor="consent-checkbox" className="cursor-pointer">
              Remember me for faster check-in at the event
            </label>
          </div>
          <button
            type="submit"
            disabled={status === 'submitting' || !firstName.trim() || !email.trim()}
            className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium
                       hover:bg-emerald-700 transition-colors
                       disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? 'Confirming...' : 'RSVP'}
          </button>
        </form>

        {status === 'error' && (
          <p className="text-red-600 text-sm mt-3 text-center">
            Something went wrong. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}

EventRSVPView.propTypes = {
  event: PropTypes.shape({
    name: PropTypes.string,
    starts_at: PropTypes.string,
    ends_at: PropTypes.string,
    location_name: PropTypes.string,
  }),
  intentCode: PropTypes.string,
  marketSlug: PropTypes.string,
};
